import {
    BankOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Button,
    Card,
    Col,
    Progress,
    Row,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };

export default function RepriseGumVtCommissariats() {
    const [step, setStep]           = useState(0);   // 0 sélection, 1 aperçu, 2 application, 3 rapport
    const [fichier, setFichier]     = useState(null);
    const [apercu, setApercu]       = useState(null);
    const [rapport, setRapport]     = useState(null);
    const [loading, setLoading]     = useState(false);
    const [progress, setProgress]   = useState(0);
    const [jobId, setJobId]         = useState(null);
    const [jobEtat, setJobEtat]     = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    // ── Étape 1 : aperçu (synchrone, lecture seule) ───────────────────────────
    const previsualiser = async () => {
        if (!fichier) { toast.warning("Sélectionnez le fichier Excel."); return; }
        setLoading(true);
        setApercu(null);
        try {
            const form = new FormData();
            form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/completer-commissariats/preview", form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setApercu(res.data);
            setStep(1);
        } catch (err) {
            toast.error("Erreur aperçu : " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // ── Étape 2 : application réelle (asynchrone) ─────────────────────────────
    const appliquer = async () => {
        if (!fichier) return;
        setLoading(true);
        setRapport(null);
        setProgress(5);
        setJobEtat("EN_COURS");
        setStep(2);
        try {
            const form = new FormData();
            form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/completer-commissariats", form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id);
            demarrerPolling(id);
        } catch (err) {
            setLoading(false);
            setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100);
                    setJobEtat(statut.etat);
                    setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat);
                        setStep(3);
                        toast.success("Liaison des commissariats terminée !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current);
                setLoading(false);
                setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = (data) => {
        if (!data) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Sinistres avec commissariat", data.totalLignes],
            ["À lier / liés", data.resolus],
            ["Déjà liés", data.dejaLies],
            ["Sinistres introuvables", data.sinistreIntrouvable],
            ["Commissariats introuvables", data.commissariatIntrouvable],
            ["Commissariats ambigus", data.commissariatAmbigu],
            ["Durée (ms)", data.dureeMs],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const liens = [
            ["N° sinistre", "N° commissariat", "Nom (fichier)", "ID entité", "Nom (base)", "Méthode"],
            ...(data.echantillonResolus || []).map(e =>
                [e.numSinistre, e.numCommissariat, e.nomFichier, e.entiteConstatId, e.nomBase, e.methode]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(liens), "Liens");
        const errData = [["Détail"], ...(data.lignesErreur || []).map(e => [e])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errData), "Anomalies");
        XLSX.writeFile(wb, `commissariats_gumvt_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setFichier(null); setApercu(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    const colsLiens = [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre", width: 150,
          render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: "N°", dataIndex: "numCommissariat", key: "numCommissariat", width: 50,
          render: v => <Text type="secondary">{v}</Text> },
        { title: "Nom (fichier)", dataIndex: "nomFichier", key: "nomFichier",
          render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
        { title: "Nom (base)", dataIndex: "nomBase", key: "nomBase",
          render: v => <Text type="success" style={{ fontSize: 12 }}>{v}</Text> },
        { title: "Méthode", dataIndex: "methode", key: "methode", width: 110,
          render: v => v === "approximatif"
              ? <Tag color="orange">approximatif</Tag>
              : <Tag color="green">exact</Tag> },
    ];

    const Indicateurs = ({ d }) => (
        <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Avec commissariat" value={d.totalLignes} valueStyle={{ fontSize: 18 }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title={d.apercu ? "À lier" : "Liés"} value={d.resolus}
                    valueStyle={{ color: "#52c41a", fontSize: 18 }} prefix={<CheckCircleFilled />} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Déjà liés" value={d.dejaLies}
                    valueStyle={{ color: "#8c8c8c", fontSize: 18 }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Sin. introuv." value={d.sinistreIntrouvable}
                    valueStyle={{ color: d.sinistreIntrouvable > 0 ? "#faad14" : "#52c41a", fontSize: 18 }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Commiss. introuv." value={d.commissariatIntrouvable}
                    valueStyle={{ color: d.commissariatIntrouvable > 0 ? "#faad14" : "#52c41a", fontSize: 18 }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
                <Statistic title="Ambigus" value={d.commissariatAmbigu}
                    valueStyle={{ color: d.commissariatAmbigu > 0 ? "#ff4d4f" : "#52c41a", fontSize: 18 }} />
            </Col>
        </Row>
    );

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <BankOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Compléter les commissariats — GUM / VT
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Relie chaque sinistre à son entité de constat (aperçu avant écriture)
                        </Text>
                    </div>
                </Space>

                <Steps
                    current={step}
                    style={{ marginBottom: 32 }}
                    items={[
                        { title: "Sélection", description: "Fichier Excel" },
                        { title: "Aperçu", description: "Simulation" },
                        { title: "Application", description: "Écriture en base" },
                        { title: "Rapport", description: "Résultats" },
                    ]}
                />

                {step === 0 && (
                    <Card>
                        <Alert
                            type="info" showIcon style={{ marginBottom: 16 }}
                            message="Lit les feuilles « commissariats » (n° → nom) et « Sinistres » (n° commissariat), puis relie chaque sinistre à son entité de constat en base par le nom. L'aperçu ne modifie rien."
                        />
                        <Dragger
                            accept=".xlsx,.xls"
                            multiple={false}
                            beforeUpload={(f) => { setFichier(f); return false; }}
                            onRemove={() => setFichier(null)}
                            maxCount={1}
                            style={{ marginBottom: 24 }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#242166" }} />
                            </p>
                            <p className="ant-upload-text">Cliquez ou déposez le fichier Excel Pool GUM/VT</p>
                            <p className="ant-upload-hint">Le même fichier que l'import des sinistres</p>
                        </Dragger>

                        {fichier && (
                            <Alert
                                type="success" showIcon style={{ marginBottom: 16 }}
                                message={
                                    <Space>
                                        <FileExcelOutlined style={{ color: "#217346" }} />
                                        <Text strong>{fichier.name}</Text>
                                        <Text type="secondary">({(fichier.size / 1024).toFixed(1)} Ko)</Text>
                                    </Space>
                                }
                            />
                        )}

                        <div style={{ textAlign: "right" }}>
                            <Button
                                type="primary" size="large" icon={<EyeOutlined />}
                                loading={loading} disabled={!fichier}
                                onClick={previsualiser} style={{ background: "#242166" }}
                            >
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && apercu && (
                    <div>
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 20 }}
                            icon={<ExclamationCircleOutlined />}
                            message="Aperçu — aucune écriture effectuée"
                            description={`${apercu.resolus} sinistre(s) seront reliés à leur commissariat${apercu.resolusApprox > 0 ? `, dont ${apercu.resolusApprox} par correspondance approximative (à vérifier dans le tableau, colonne « Méthode »)` : ""}. Vérifiez l'échantillon ci-dessous puis appliquez.`}
                        />

                        <Card style={{ marginBottom: 20 }}><Indicateurs d={apercu} /></Card>

                        {apercu.echantillonResolus?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des liens à créer ({apercu.echantillonResolus.length})</Text>}>
                                <Table
                                    dataSource={apercu.echantillonResolus.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsLiens}
                                    size="small"
                                    pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 320 }}
                                />
                            </Card>
                        )}

                        {apercu.lignesErreur?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Space><WarningOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Non résolus ({apercu.lignesErreur.length})</Text></Space>}>
                                <Table
                                    dataSource={apercu.lignesErreur.map((msg, i) => ({ key: i, message: msg }))}
                                    columns={[{ title: "Détail", dataIndex: "message", key: "message",
                                        render: v => <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> }]}
                                    size="small"
                                    pagination={{ pageSize: 15, size: "small" }}
                                    scroll={{ y: 240 }}
                                />
                            </Card>
                        )}

                        <Space>
                            <Button onClick={reinitialiser}>Annuler</Button>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(apercu)}>
                                Exporter l'aperçu
                            </Button>
                            <Button type="primary" icon={<ThunderboltOutlined />}
                                disabled={apercu.resolus === 0} onClick={appliquer}
                                style={{ background: "#242166" }}>
                                Appliquer en base ({apercu.resolus})
                            </Button>
                        </Space>
                    </div>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 16 }}>Liaison des commissariats en cours…</Text>
                                    </div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Job ID : <Text code>{jobId}</Text></Text>
                                            <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginLeft: 8 }}>
                                                {ETAT_LABELS[jobEtat] || jobEtat}
                                            </Tag>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 3 && rapport && (
                    <div>
                        <Alert
                            type="success" showIcon icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                            message={`${rapport.resolus} sinistre(s) reliés à leur commissariat`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s`}
                            style={{ marginBottom: 20 }}
                        />
                        <Card style={{ marginBottom: 20 }}><Indicateurs d={rapport} /></Card>
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(rapport)}>
                                Exporter le rapport
                            </Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser}
                                style={{ background: "#242166" }}>
                                Nouvelle complétion
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
